import React, {useState} from 'react';
import {useLocation, useParams} from 'react-router-dom';
import qs from "query-string";
import Switch from 'react-switch';
import Select from 'react-select';
import {
  useForm,
  Controller
} from 'react-hook-form';
import { useSystemMessage } from '../../hooks/useSystemMessage';
import useLoading from '../../hooks/useLoading';
import useAppRouter from "../../hooks/useAppRouter";

import { roomApi } from '../../apis/admin/room/roomApi';
import { roomSpaceMasterApi } from '../../apis/admin/room/roomSpaceMasterApi';
import { roomVhDefMasterApi } from '../../apis/admin/room/roomVhDefMasterApi';
import { roomMenusetApi } from '../../apis/admin/room/roomMenusetApi';

import { SelectOption } from '../../types/admin/room';
import {config} from "../../libs/config/config";
import {FormValidationType, makeRegisterOptions} from "../../libs/reactHookForm/makeRegisterOptions";
import {ValidationErrorMessages} from "../../components/pages/common/ValidationErrorMessages";
import {ApiClientError, ResponseCode} from "../../apis/errors";
import {TabNav} from "../../components/pages/admin/RoomEdit/TabNav";
import {Tab} from "../../components/pages/admin/RoomEdit/TabNavItem";
import {logError} from "../../libs/utils/logError";

interface RoomEditRouteParams {
    id: string;
}

export const RoomEditPage: React.FC = () => {
  const { id } = useParams<RoomEditRouteParams>();
  const { goto } = useAppRouter();

  const withLoading = useLoading();
  const location = useLocation();
  const { success, warn } = useSystemMessage();

  const [currentTab, setCurrentTab] = React.useState<Tab>(Tab.BASIC);

  const {
    register,
    getValues,
    handleSubmit,
    formState: { errors },
    control,
    setValue,
  } = useForm({
    reValidateMode: 'onSubmit',
  });

  const [vhSpaceSetList, setVHSpaceSetLists] = React.useState<SelectOption[]>([]);
  const [vhDefinesList, setVHDefinesLists] = React.useState<SelectOption[]>([]);
  const [menusetList, setMenusetLists] = React.useState<SelectOption[]>([]);

  const [ apiClientError, setApiClientError ] = useState<ApiClientError | null>(null);

  const loadRoom = async () => {
    const res = await roomApi.show(Number(id), {});

    setValue("roomName", res.room.roomName);
    setValue("content", res.room.content);
    setValue("enabled", res.room.enabledFlg);
    setValue("adminInfo", res.room.adminInfo);
    setValue("meetingUrl", res.room.meetingUrl);
    setValue("vhSpaceSetId", res.room.vhSpaceSetId);
    setValue("vhDefinesId", res.room.vhDefSetId);
    setValue("menuSetId", res.room.menuSetId);
  }

  const loadRoomSpaceMaster = async () => {
    const result = await roomSpaceMasterApi
    .index({});

    const selectItemList: Array<SelectOption> = [];
    for (let i = 0; i < result.spaces.length; i +=1 ) {
      selectItemList.push({
        value: result.spaces[i].id,
        label: result.spaces[i].name
      });
    }
    setVHSpaceSetLists(selectItemList);
  }

  const loadRoomVhDefMaster = async () => {
    const result = await roomVhDefMasterApi
    .index({});
    const selectItemList: Array<SelectOption> = [];
    for (let i = 0; i < result.vhDefines.length; i +=1 ) {
      selectItemList.push({
        value: result.vhDefines[i].id,
        label: result.vhDefines[i].name
      });
    }

    setVHDefinesLists(selectItemList);
  }

  const loadMenusetMaster = async () => {
    const result = await roomMenusetApi
    .index(
      undefined,
      0,
      undefined,
      undefined,
      {}
    );
    const selectItemList: Array<SelectOption> = [];
    for (let i = 0; i < result.menuSetList.length; i +=1 ) {
      selectItemList.push({
        value: result.menuSetList[i].id,
        label: result.menuSetList[i].name
      });
    }
    setMenusetLists(selectItemList);
  }

  const handle3DModelPreviewClick = async () => {
    const vhSpaceSetId = getValues('vhSpaceSetId');
    if (vhSpaceSetId === undefined) {
      await warn('3Dモデルを選択してください');
      return;
    }

    const previewUrl = config.room.page.preview.url;
    const queryParams = {
      mainasset: config.room.file.threeDModel.url?.replace('<id>', vhSpaceSetId),
    }

    window.open(`${previewUrl}?${qs.stringify(queryParams)}`, '_blank');
  }

  const handleVhDefinePreviewClick = async () => {
    const vhSpaceSetId = getValues('vhSpaceSetId');
    if (vhSpaceSetId === undefined) {
      await warn('3Dモデルを選択してください');
      return;
    }

    const vhDefinesId = getValues('vhDefinesId');
    if (vhDefinesId === undefined) {
      await warn('VH定義を選択してください');
      return;
    }

    const previewUrl = config.room.page.preview.url;
    const queryParams = {
      mainasset: config.room.file.threeDModel.url?.replace('<id>', vhSpaceSetId),
      chat: config.room.file.balloonChatScenario.url?.replace('<id>', vhDefinesId),
    }

    window.open(`${previewUrl}?${qs.stringify(queryParams)}`, '_blank');
  }

  const handleSave = async () => {
    withLoading(async () => {
      setApiClientError(null);
      await handleSubmit(async (data: any) => {
        try {
          await roomApi.update(
            Number(id),
            {
              roomName: data.roomName,
              content: data.content,
              adminInfo: data.adminInfo,
              meetingUrl: data.meetingUrl,
              enabledFlg: data.enabled,
              vhSpaceSetId: data.vhSpaceSetId,
              vhDefSetId: data.vhDefinesId,
              menuSetId: data.menuSetId
            },
            {}
          );

          await success('更新しました。');
          goto('roomList');

        } catch (e) {
          await logError(
            location.pathname,
            'ROOMの更新に失敗しました。',
            e
          );

          if (e instanceof ApiClientError) {
            setApiClientError(e);
            return;
          }

          goto('internalServerError');
        }
      })();
    });
  };

  React.useEffect(() => {
    const fetch = async () => {
      withLoading(async () => {
        try {
          await Promise.all( [
            loadRoom(),
            loadRoomSpaceMaster(),
            loadRoomVhDefMaster(),
            loadMenusetMaster()
          ]);

        } catch (e) {
          await logError(
            location.pathname,
            'ROOM情報の読み込みに失敗しました。',
            e
          );

          if ((e instanceof ApiClientError) && (e.code === ResponseCode.NOT_FOUND)) {
            goto('notFoundError');
            return;
          }

          goto('internalServerError');
        }
      });
    };
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div className="bg-sub flex flex-col min-h-full">
        <div className="text-xl text-primary font-semibold py-6 px-8">ROOM編集</div>
        <div className="text-danger text-xs mb-4 px-6">
          { apiClientError?.message }
          { (Object.keys(errors).length > 0) && '入力エラーがあります' }
        </div>

        <div className="mx-8 shadow border border-white flex flex-col">
          <TabNav
            {...{
              currentTab,
              setCurrentTab,
            }}
          />
          <form id="roomForm">
            <div className="p-6 flex-grow">
              <div className={currentTab === Tab.BASIC ? 'block' : 'hidden'} id="link1">


                { /* ROOM名 */ }
                <div className="mb-4">
                  <label className="block" htmlFor="roomName">
                    <div className="text-sm font-semibold text-primary mb-1">
                      <span>ROOM名</span>
                      <span className="ml-2 required">必須</span>
                    </div>
                    <input
                      /* eslint-disable-next-line react/jsx-props-no-spreading */
                      {...register('roomName',
                        makeRegisterOptions('ROOM名', [
                          { type: FormValidationType.REQUIRED },
                          { type: FormValidationType.MAX_STRING_LENGTH, value: 255 },
                        ])
                      )}
                      className="text-field"
                      id="roomName"
                      type="text"
                      maxLength={255}
                    />
                    <div className="mt-1 text-danger text-xs">
                      {errors.roomName?.message}
                    </div>
                    <ValidationErrorMessages
                      {...{
                        fieldErrors: apiClientError?.errors,
                        field: 'roomName',
                      }}
                    />
                  </label>
                </div>

                { /* 説明 */ }
                <div className="mb-4">
                  <label className="block" htmlFor="content">
                    <div className="text-sm font-semibold text-primary mb-1">
                      <span>説明</span>
                    </div>
                    <textarea
                      /* eslint-disable-next-line react/jsx-props-no-spreading */
                      {...register('content',
                        makeRegisterOptions('説明', [
                          { type: FormValidationType.MAX_STRING_LENGTH, value: 1024 },
                        ])
                      )}
                      className="text-field w-full"
                      id="content"
                      name="content"
                      rows={5}
                      maxLength={1024}
                    />
                    <div className="mt-1 text-danger text-xs">
                      {errors.content?.message}
                    </div>
                    <ValidationErrorMessages
                      {...{
                        fieldErrors: apiClientError?.errors,
                        field: 'content',
                      }}
                    />
                  </label>
                </div>

                <div className="mb-4">
                  <Controller
                    name="enabled"
                    control={control}
                    /* eslint-disable-next-line react/jsx-boolean-value */
                    defaultValue={true}
                    render={({field: { onChange, value }}) => (
                      <Switch
                        onChange={onChange}
                        checked={value}
                        className="react-switch"
                        width={70}
                        uncheckedIcon={
                          <span
                            className="inline-block align-middle font-bold text-white"
                            style={{
                              fontSize: 15,
                              paddingLeft: 3,
                            }}
                          >
                            無効
                          </span>
                        }
                        checkedIcon={
                          <span
                            className="inline-block align-middle font-bold text-white"
                            style={{
                              fontSize: 15,
                              paddingLeft: 8,
                            }}
                          >
                            有効
                          </span>
                        }
                      />)}
                  />
                </div>

                <div className="mb-4">
                  <label className="block" htmlFor="adminInfo">
                    <div className="text-sm font-semibold text-primary mb-1">
                      <span>運営会社・運営責任者</span>
                      <span className="ml-2 required">必須</span>
                    </div>
                    <input
                      /* eslint-disable-next-line react/jsx-props-no-spreading */
                      {...register('adminInfo',
                        makeRegisterOptions('運営会社・運営責任者', [
                          { type: FormValidationType.REQUIRED },
                          { type: FormValidationType.MAX_STRING_LENGTH, value: 255 },
                        ])
                      )}
                      className="text-field"
                      id="adminInfo"
                      type="text"
                      maxLength={255}
                    />
                    <div className="mt-1 text-danger text-xs">
                      {errors.adminInfo?.message}
                    </div>
                    <ValidationErrorMessages
                      {...{
                        fieldErrors: apiClientError?.errors,
                        field: 'adminInfo',
                      }}
                    />
                  </label>
                </div>

                <div className="mb-4">
                  <label className="block" htmlFor="meetingUrl">
                    <div className="text-sm font-semibold text-primary mb-1">
                      <span>会議URL</span>
                    </div>
                    <input
                      /* eslint-disable-next-line react/jsx-props-no-spreading */
                      {...register('meetingUrl',
                        makeRegisterOptions('会議URL', [
                          { type: FormValidationType.MAX_STRING_LENGTH, value: 255 },
                          { type: FormValidationType.URL, typeName: 'url' },
                        ])
                      )}
                      className="text-field"
                      id="meetingUrl"
                      type="text"
                      maxLength={255}
                    />
                    <div className="mt-1 text-danger text-xs">
                      {errors.meetingUrl?.message}
                    </div>
                    <ValidationErrorMessages
                      {...{
                        fieldErrors: apiClientError?.errors,
                        field: 'meetingUrl',
                      }}
                    />
                  </label>
                </div>
              </div>

              <div className={currentTab === Tab.THREE_D_SPACE ? 'block' : 'hidden'} id="link2">
                <div className="mb-4">
                  <div className="text-sm font-semibold text-primary mb-1">
                    <span>3Dモデル</span>
                    <span className="ml-2 required">必須</span>
                  </div>
                  <div className="flex items-center">
                    <Controller
                      name="vhSpaceSetId"
                      control={control}
                      rules={
                        makeRegisterOptions('3Dモデル', [
                          { type: FormValidationType.REQUIRED },
                        ])
                      }
                      render={({field: { onChange, value }}) => (
                        <Select
                          options={vhSpaceSetList}
                          isSearchable
                          className="w-1/2 text-sm"
                          value={vhSpaceSetList.find(c => c.value === value)}
                          onChange={(val) => {
                            if (val !== null) {
                              onChange(val.value);
                            }
                          }}
                        />
                      )}
                    />
                    <div>
                      <button
                        type="button"
                        className="btn-primary bg-blue-500 ml-1"
                        onClick={handle3DModelPreviewClick}
                      >
                        プレビュー
                      </button>
                    </div>
                  </div>
                  <div className="mt-1 text-danger text-xs">
                    {errors.vhSpaceSetId?.message}
                  </div>
                  <ValidationErrorMessages
                    {...{
                      fieldErrors: apiClientError?.errors,
                      field: 'vhSpaceSetId',
                    }}
                  />
                </div>
              </div>

              <div className={currentTab === Tab.VH_DEFINE ? 'block' : 'hidden'} id="link3">
                <div className="mb-4">
                  <div className="text-sm font-semibold text-primary mb-1">
                    <span>VH定義</span>
                    <span className="ml-2 required">必須</span>
                  </div>
                  <div className="flex items-center">
                    <Controller
                      name="vhDefinesId"
                      control={control}
                      rules={
                        makeRegisterOptions('VH定義', [
                          {type: FormValidationType.REQUIRED},
                        ])
                      }
                      render={({field: { onChange, value }}) => (
                        <Select
                          options={vhDefinesList}
                          isSearchable
                          className="w-1/2 text-sm"
                          value={vhDefinesList.find(c => c.value === value)}
                          onChange={(val) => {
                            if (val !== null) {
                              onChange(val.value);
                            }
                          }}
                        />
                      )}
                    />
                    <div>
                      <button
                        type="button"
                        className="btn-primary bg-blue-500 ml-1"
                        onClick={handleVhDefinePreviewClick}
                      >
                        プレビュー
                      </button>
                    </div>
                  </div>
                  <div className="mt-1 text-danger text-xs">
                    {errors.vhDefinesId?.message}
                  </div>
                  <ValidationErrorMessages
                    {...{
                      fieldErrors: apiClientError?.errors,
                      field: 'vhDefinesId',
                    }}
                  />
                </div>
              </div>

              <div className={currentTab === Tab.MENU_SET ? 'block' : 'hidden'} id="link4">
                <div className="mb-4">
                  <div className="text-sm font-semibold text-primary mb-1">
                    <span>メニューセット</span>
                  </div>
                  <div className="text-sm">
                    { menusetList.find(c => c.value === getValues('menuSetId'))?.label }
                  </div>
                  <div className="mt-1 text-danger text-xs">
                    {errors.menuSetId?.message}
                  </div>
                  <ValidationErrorMessages
                    {...{
                      fieldErrors: apiClientError?.errors,
                      field: 'menuSetId',
                    }}
                  />
                </div>
              </div>
            </div>
          </form>
        </div>

        <div className="sticky flex bottom-0 bg-sub mt-auto py-4 px-8 border-t border-primary">
          <button
            type="button"
            className="btn-sub"
            onClick={() => {
              goto('roomList');
            }}
          >
            キャンセル
          </button>
          <div className="ml-auto">
            <button
              type="button"
              className="btn-primary"
              form="roomForm"
              onClick={handleSave}
            >
              保存
            </button>
          </div>
        </div>
      </div>
    </>
  );
};